import { useMemo } from 'react';
import { v4 as uuid } from 'uuid';

import { flowNodeColor } from 'fable/flowEditor';
import type { ChatExtraData } from 'fable/flowEditor/components/flowManager/canvas/types/extraData';
import { Text } from 'flow-builder/node/text';
import { TextBreak } from 'flow-builder/node/textBreak';
import { TextContent } from 'flow-builder/node/textContent';
import { hexStringToNumber } from 'flow-builder/renderers/common/colors';
import type {
  GenericNodeAttributes,
  GenericNodeProps,
} from 'flow-builder/types/node';

import HideStrategyModal from '../../../containers/modals/hidestrategy';
import { HideStrategy } from '../../types';

type StrategyNeverPL = {
  strategy: HideStrategy.NEVER;
  shownNodeId: string;
};

type StrategyHidePL = {
  strategy: HideStrategy.ALL_AGENTS_AWAY | HideStrategy.ALL_AGENTS_OFFLINE;
  hiddenNodeId: string;
  shownNodeId: string;
};

type StrategyEWTPL = {
  strategy: HideStrategy.EXPECTED_WAITING_TIME;
  hiddenNodeId: string;
  shownNodeId: string;
  ewtThreshold: number;
};

export type Payload = StrategyEWTPL | StrategyHidePL | StrategyNeverPL;

type HideStrategyNodeAttributes = GenericNodeAttributes<Payload, ChatExtraData>;
type HideStrategyNodeProps = GenericNodeProps<Payload, ChatExtraData>;

export const HIDE_STRATEGY_NAMES = {
  [HideStrategy.ALL_AGENTS_AWAY]:
    'Hide when all agents are either away or offline',
  [HideStrategy.ALL_AGENTS_OFFLINE]: 'Hide when all agents are offline',
  [HideStrategy.NEVER]: 'Never hide',
  [HideStrategy.EXPECTED_WAITING_TIME]:
    'Hide when expected waiting time is above threshold',
};

const translatePayload: HideStrategyNodeAttributes['translatePayload'] = ({
  payload,
}) => {
  const ports =
    payload.strategy !== HideStrategy.NEVER
      ? [
          {
            id: uuid(),
            label: 'Hidden',
            connection: payload.hiddenNodeId || uuid(),
          },
        ]
      : [];

  return [
    ...ports,
    {
      id: uuid(),
      label: 'Shown',
      connection: payload.shownNodeId || uuid(),
    },
  ];
};

const updateConnection: HideStrategyNodeAttributes['updateConnection'] = (
  node,
  connection,
  connectionId,
) => {
  const { payload } = node;
  const fieldToUpdate =
    connection.connectionLabel === 'Shown' ? 'shownNodeId' : 'hiddenNodeId';

  return {
    ...node,
    payload: {
      ...payload,
      [fieldToUpdate]: connectionId,
    },
  };
};

export const HideStrategyNodeAttributes: HideStrategyNodeAttributes = {
  iconName: 'hide-strategy',
  label: 'Hide strategy',
  color: hexStringToNumber(flowNodeColor.purple),
  translatePayload,
  updateConnection,
  NodeContent: HideStrategyNode,
  ActionModal: HideStrategyModal,
};

export function HideStrategyNode({ node }: HideStrategyNodeProps): JSX.Element {
  const { payload } = node;
  const { strategy } = payload;

  return (
    <TextContent>
      {useMemo(
        () => (
          <TextBreak>
            <Text weight="normal" text="Strategy:" />
            <Text weight="semibold" text={HIDE_STRATEGY_NAMES[strategy]} />
          </TextBreak>
        ),
        [strategy],
      )}
    </TextContent>
  );
}
